/**
 * オフライン決済キューサービス
 * ネットワーク障害時に決済データをIndexedDBに保存し、
 * 復旧時に自動同期する仕組み
 */

const DB_NAME = 'POSOfflineDB';
const DB_VERSION = 1;
const STORE_NAME = 'pendingPayments';
const SYNC_INTERVAL = 30000; // 30秒ごとに同期試行

// IndexedDBインスタンス
let db = null;

/**
 * IndexedDBを初期化
 */
const initDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('[OfflineQueue] IndexedDB初期化エラー:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.log('[OfflineQueue] IndexedDB初期化完了');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, {
          keyPath: 'id',
          autoIncrement: true,
        });
        store.createIndex('status', 'status', { unique: false });
        store.createIndex('createdAt', 'createdAt', { unique: false });
        store.createIndex('storeId', 'storeId', { unique: false });
      }
    };
  });
};

/**
 * オフライン決済をキューに追加
 * @param {Object} paymentData - 決済データ
 * @returns {Promise<number>} - キューID
 */
const enqueue = async (paymentData) => {
  const database = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const queueItem = {
      ...paymentData,
      status: 'pending',
      createdAt: new Date().toISOString(),
      retryCount: 0,
      lastError: null,
    };

    const request = store.add(queueItem);

    request.onsuccess = () => {
      console.log('[OfflineQueue] 決済をキューに追加:', request.result);
      resolve(request.result);
    };

    request.onerror = () => {
      console.error('[OfflineQueue] キュー追加エラー:', request.error);
      reject(request.error);
    };
  });
};

/**
 * 保留中の決済を取得
 * @returns {Promise<Array>} - 保留中の決済リスト
 */
const getPendingPayments = async () => {
  const database = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('status');
    const request = index.getAll('pending');

    request.onsuccess = () => {
      resolve(request.result || []);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
};

/**
 * 全ての決済を取得
 * @returns {Promise<Array>} - 全決済リスト
 */
const getAllPayments = async () => {
  const database = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => {
      resolve(request.result || []);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
};

/**
 * 決済ステータスを更新
 * @param {number} id - キューID
 * @param {string} status - 新しいステータス
 * @param {Object} additionalData - 追加データ
 */
const updatePaymentStatus = async (id, status, additionalData = {}) => {
  const database = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const item = getRequest.result;
      if (!item) {
        reject(new Error('キューアイテムが見つかりません'));
        return;
      }

      const updatedItem = {
        ...item,
        status,
        updatedAt: new Date().toISOString(),
        ...additionalData,
      };

      const putRequest = store.put(updatedItem);

      putRequest.onsuccess = () => {
        resolve(updatedItem);
      };

      putRequest.onerror = () => {
        reject(putRequest.error);
      };
    };

    getRequest.onerror = () => {
      reject(getRequest.error);
    };
  });
};

/**
 * 同期済みの決済を削除
 * @param {number} id - キューID
 */
const removePayment = async (id) => {
  const database = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);

    request.onsuccess = () => {
      console.log('[OfflineQueue] 決済を削除:', id);
      resolve();
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
};

/**
 * 古い同期済み決済をクリーンアップ（7日以上前）
 */
const cleanup = async () => {
  const database = await initDB();
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('status');
    const request = index.openCursor(IDBKeyRange.only('synced'));

    let deletedCount = 0;

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        if (cursor.value.createdAt < sevenDaysAgo) {
          cursor.delete();
          deletedCount++;
        }
        cursor.continue();
      } else {
        console.log(`[OfflineQueue] ${deletedCount}件の古い決済をクリーンアップ`);
        resolve(deletedCount);
      }
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
};

/**
 * 保留中の決済をサーバーに同期
 * @param {Function} syncFunction - 同期関数（APIコール）
 */
const syncPendingPayments = async (syncFunction) => {
  if (!navigator.onLine) {
    console.log('[OfflineQueue] オフライン状態のため同期をスキップ');
    return { synced: 0, failed: 0 };
  }

  const pendingPayments = await getPendingPayments();

  if (pendingPayments.length === 0) {
    return { synced: 0, failed: 0 };
  }

  console.log(`[OfflineQueue] ${pendingPayments.length}件の保留中決済を同期開始`);

  let syncedCount = 0;
  let failedCount = 0;

  for (const payment of pendingPayments) {
    try {
      // リトライ回数チェック（最大5回）
      if (payment.retryCount >= 5) {
        console.warn(`[OfflineQueue] 決済${payment.id}のリトライ上限に達しました`);
        await updatePaymentStatus(payment.id, 'failed', {
          lastError: 'リトライ上限（5回）に達しました',
        });
        failedCount++;
        continue;
      }

      // 同期処理を実行
      const result = await syncFunction(payment);

      if (result.success) {
        await updatePaymentStatus(payment.id, 'synced', {
          syncedAt: new Date().toISOString(),
          serverResponse: result,
        });
        syncedCount++;
        console.log(`[OfflineQueue] 決済${payment.id}の同期成功`);
      } else {
        throw new Error(result.error || '同期に失敗しました');
      }
    } catch (error) {
      console.error(`[OfflineQueue] 決済${payment.id}の同期失敗:`, error);
      await updatePaymentStatus(payment.id, 'pending', {
        retryCount: payment.retryCount + 1,
        lastError: error.message,
      });
      failedCount++;
    }
  }

  console.log(`[OfflineQueue] 同期完了: ${syncedCount}件成功, ${failedCount}件失敗`);
  return { synced: syncedCount, failed: failedCount };
};

/**
 * 定期同期を開始
 * @param {Function} syncFunction - 同期関数
 * @returns {number} - インターバルID（停止用）
 */
let syncIntervalId = null;

const startPeriodicSync = (syncFunction) => {
  if (syncIntervalId) {
    console.warn('[OfflineQueue] 定期同期は既に開始されています');
    return syncIntervalId;
  }

  // オンライン復帰時に即座に同期
  const handleOnline = () => {
    console.log('[OfflineQueue] オンライン復帰を検出、同期を開始');
    syncPendingPayments(syncFunction);
  };

  window.addEventListener('online', handleOnline);

  // 定期的に同期を試行
  syncIntervalId = setInterval(() => {
    syncPendingPayments(syncFunction);
  }, SYNC_INTERVAL);

  // 初回同期
  syncPendingPayments(syncFunction);

  console.log('[OfflineQueue] 定期同期を開始しました');

  return {
    intervalId: syncIntervalId,
    stop: () => {
      if (syncIntervalId) {
        clearInterval(syncIntervalId);
        syncIntervalId = null;
      }
      window.removeEventListener('online', handleOnline);
      console.log('[OfflineQueue] 定期同期を停止しました');
    },
  };
};

/**
 * オフライン状態かどうかを判定
 */
const isOffline = () => !navigator.onLine;

/**
 * キューの状態サマリを取得
 */
const getQueueSummary = async () => {
  const allPayments = await getAllPayments();

  const summary = {
    total: allPayments.length,
    pending: 0,
    synced: 0,
    failed: 0,
    totalAmount: 0,
    pendingAmount: 0,
  };

  allPayments.forEach((payment) => {
    summary[payment.status] = (summary[payment.status] || 0) + 1;
    summary.totalAmount += payment.total || payment.amount || 0;
    if (payment.status === 'pending') {
      summary.pendingAmount += payment.total || payment.amount || 0;
    }
  });

  return summary;
};

export {
  initDB,
  enqueue,
  getPendingPayments,
  getAllPayments,
  updatePaymentStatus,
  removePayment,
  cleanup,
  syncPendingPayments,
  startPeriodicSync,
  isOffline,
  getQueueSummary,
};

const offlinePaymentQueue = {
  initDB,
  enqueue,
  getPendingPayments,
  getAllPayments,
  updatePaymentStatus,
  removePayment,
  cleanup,
  syncPendingPayments,
  startPeriodicSync,
  isOffline,
  getQueueSummary,
};

export default offlinePaymentQueue;
